import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useRestoreSigningProcess } from '@auth/hooks/useRestoreSigningProcess'
import { storeSigningIntent } from '@auth/utils/storeSigningIntent'
import SplitButton from '@common/components/SplitButton/SplitButton'
import Toast from '@common/components/Toast'
import { HTTPValidationError, ReadTaskSummaryData } from '@common/config/api/client'
import { SIZING } from '@common/theme/sizing'
import { SPACING } from '@common/theme/spacing'
import { ArrowBackRounded } from '@mui/icons-material'
import { Box, Button, Card, CircularProgress, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { VIEWER_OPTIONS } from '../../constants'
import { UsePatchTaskMutation } from '../../hooks/UsePatchTaskMutation'
import { useReadTaskESignMutation } from '../../hooks/useReadTaskESignMutation'
import EcrfMenu from './EcrfMenu'
import EcrfSurvey, { EcrfSurveyRef } from './EcrfSurvey'

export default function EcrfLayout({
  timepoints,
  readTaskId,
  read_task_summary_metadata,
  isSignNSendDisabled,
  onClose,
  onFocusChild,
  readerSetEndTime,
  pauseReaderActiveTime,
  isRawDataEnabled,
}: {
  timepoints: ReadTaskSummaryData['timepoints']
  readTaskId: string
  read_task_summary_metadata: ReadTaskSummaryData['read_task_summary_metadata']
  isSignNSendDisabled: boolean
  onClose?: () => void
  onFocusChild?: (index: number) => void
  readerSetEndTime?: (end_time: Date | number) => void
  pauseReaderActiveTime?: () => void
  isRawDataEnabled?: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState('ecrf')
  const { user } = useExtendedAuth()
  const surveyRef = useRef<EcrfSurveyRef>(null)
  const [isSigningIn, setIsSigningIn] = useState(false)
  const { signinRedirect } = useAuth()
  const { studyId } = useParams<{ studyId: string }>()
  const readTaskSignMutation = useReadTaskESignMutation()

  // State to hold the initial eCRF answers
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [initialEcrfAnswers, setInitialEcrfAnswers] = useState<any>(null)
  const [disabledViewerOptions, setDisabledViewerOptions] = useState<number[] | undefined>([])

  const defaultViewerIndex = (() => {
    if (isRawDataEnabled) return 2
    if (read_task_summary_metadata?.type_of_gcis_viewer?.includes('bluerock')) return 3
    return 0
  })()

  useEffect(() => {
    setDisabledViewerOptions([])
    const disabledViewers = []
    if (read_task_summary_metadata?.show_raw_data) {
      disabledViewers.push(0)
    }
    if (!read_task_summary_metadata?.type_of_gcis_viewer?.includes('fusion')) {
      disabledViewers.push(1)
    }
    if (!read_task_summary_metadata?.type_of_gcis_viewer?.includes('bluerock')) {
      disabledViewers.push(3)
    }
    setDisabledViewerOptions(disabledViewers)
  }, [read_task_summary_metadata])

  useEffect(() => {
    const currentSubmission = timepoints
      .flatMap((timepoint) => timepoint.reader_submissions)
      .find((submission) => submission.read_task_id === readTaskId)

    if (currentSubmission?.ecrf_form_submission?.answers) {
      try {
        // Assuming answers are stored as a JSON string
        const parsedAnswers = JSON.parse(currentSubmission.ecrf_form_submission.answers)
        setInitialEcrfAnswers(parsedAnswers)
      } catch (error) {
        console.error('Error parsing eCRF answers:', error)
        setInitialEcrfAnswers(null) // Handle parsing errors
      }
    } else {
      setInitialEcrfAnswers(null) // No answers found for this task
    }
  }, [readTaskId, timepoints])

  const { isSigningInProgress } = useRestoreSigningProcess(readTaskSignMutation, {
    successMessage: 'eCRF survey data is signed and submitted successfully',
    errorMessage: 'Failed to submit eCRF survey data',
  })

  const getSignerName = () => {
    const firstName = user?.profile.given_name || ''
    const lastName = user?.profile.family_name || ''
    const fullName = `${firstName} ${lastName}`.trim()

    const signerName = fullName || user?.profile.email || user?.profile.preferred_username || ''

    return signerName
  }

  const handleSignAndSend = async () => {
    if (readerSetEndTime && pauseReaderActiveTime) {
      pauseReaderActiveTime()
      readerSetEndTime(Date.now())
    }
    setIsSigningIn(true)

    const payload = {
      type: 'reader_task_submission',
      read_task_submission: {
        adjudication_justification_id: null,
        justification_comment: '',
      },
    }

    sessionStorage.setItem(
      'reader_modal_state',
      JSON.stringify({
        taskId: readTaskId,
      }),
    )

    storeSigningIntent(payload, {
      queryParams: {
        read_task_id: readTaskId,
        study_id: studyId!,
      },
      signer_name: getSignerName(),
    })

    await signinRedirect({
      prompt: 'login',
      max_age: 0,
      redirect_uri: window.location.href,
    })
  }
  const { mutateAsync: saveEcrf } = UsePatchTaskMutation()

  const handleSaveEcrfSubmission = async () => {
    const surveyData = surveyRef.current && surveyRef.current.getSurveyData()
    await saveEcrf({
      query: {
        read_task_id: readTaskId,
        study_id: studyId!,
      },
      body: {
        action: 'AUTOSAVE',
        form_submission: {
          answers: JSON.stringify(surveyData),
        },
      },
    })
      .then(() => {
        toast(<Toast message="Submitting eCRF survey data" variant="success" data-testid="success-toast" />)
      })
      .catch((error: AxiosError<HTTPValidationError>) => {
        const errorMessage = Array.isArray(error.response?.data?.detail)
          ? error.response?.data?.detail.map((err) => err.msg).join(', ')
          : error.response?.data?.detail || error.message
        toast(<Toast message={errorMessage} variant="error" data-testid="error-toast" />)
      })
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          zIndex: 20,
        }}
      >
        <Stack
          direction="row"
          paddingX={4}
          justifyContent="space-between"
          alignItems="center"
          height={56}
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <Stack>
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
              }}
            >
              {read_task_summary_metadata.timepoint_name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Task ID {read_task_summary_metadata.task_pseudo_id}
            </Typography>
          </Stack>
          <Stack>
            <Button variant="text" onClick={onClose}>
              <Typography
                variant="button"
                sx={{
                  color: 'text.primary',
                }}
              >
                Close
              </Typography>
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Grid container size={SIZING.gridSizeXxl} paddingX={12} paddingY={10}>
        <Grid size={SIZING.gridSizeXxl - SIZING.gridSizeS}>
          <Stack
            spacing={SPACING.spacingMd}
            sx={{
              width: '100%',
              '.sd-body.sd-body--static': {
                padding: '0 !important',
              },
            }}
          >
            <EcrfSurvey
              ref={surveyRef}
              selectedItem={selectedItem}
              timepoints={timepoints}
              read_task_summary_metadata={read_task_summary_metadata}
              initialAnswers={initialEcrfAnswers}
            />
            <Button
              size="large"
              variant="contained"
              sx={{
                color: 'text.primary',
                p: SPACING.spacingMd,
                alignSelf: 'flex-end',
              }}
              onClick={handleSaveEcrfSubmission}
            >
              <Stack direction="row" alignItems="center" justifyContent="start" spacing={SPACING.minSpacing}>
                <Typography
                  variant="button"
                  sx={{
                    color: 'primary.contrastText',
                  }}
                >
                  {' '}
                  Save{' '}
                </Typography>
              </Stack>
            </Button>
          </Stack>
        </Grid>
        <Grid size={SIZING.gridSizeS}>
          <Card sx={{ p: SPACING.minSpacing, borderRadius: SPACING.borderRadiusXxl }} elevation={1}>
            <EcrfMenu
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              timepoints={timepoints}
            />
          </Card>
        </Grid>
      </Grid>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: '100%',
          zIndex: 20,
        }}
      >
        <Stack
          direction="row"
          paddingX={4}
          justifyContent="space-between"
          alignItems="center"
          height={56}
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          {read_task_summary_metadata.viewer_selection !== 'specialized' ? (
            <Stack>
              <SplitButton
                options={VIEWER_OPTIONS}
                onFocusChild={onFocusChild}
                disabledOptions={disabledViewerOptions}
                isRawDataEnabled={isRawDataEnabled}
                defaultIndex={defaultViewerIndex}
              />
            </Stack>
          ) : (
            <Button variant="text" onClick={onClose}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  color: 'text.primary',
                }}
              >
                <ArrowBackRounded />
                <Typography variant="button">Previous step</Typography>
              </Stack>
            </Button>
          )}

          {selectedItem === 'ecrf' ? (
            <Button
              size="large"
              variant="contained"
              disabled={isSignNSendDisabled || isSigningInProgress || isSigningIn}
              sx={{
                backgroundColor: 'primary.main',
              }}
              onClick={handleSignAndSend}
              startIcon={isSigningInProgress || isSigningIn ? <CircularProgress size={20} color="inherit" /> : null}
            >
              <Typography
                variant="button"
                sx={{
                  color: 'primary.contrastText',
                }}
              >
                {isSigningInProgress || isSigningIn ? 'Signing...' : 'Sign and send'}
              </Typography>
            </Button>
          ) : (
            <Button
              size="large"
              sx={{
                backgroundColor: 'primary.main',
              }}
              onClick={() => {
                setIsOpen(false)
                setSelectedItem('ecrf')
              }}
            >
              <Typography
                variant="button"
                sx={{
                  color: 'primary.contrastText',
                }}
              >
                Go to eCRF
              </Typography>
            </Button>
          )}
        </Stack>
      </Box>
    </>
  )
}
